import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import {
  selectFilePrices,
  selectFilesInBasket,
  selectLookBookProductsInBasket,
  selectPhotoGroups,
  selectPhotosList,
  selectPriceToAddToEveryFileInLookBookProduct,
  selectPriceToAddToEveryFileInProduct,
  selectPriceToAddToFirstPrint,
  selectPrintsInBasket,
  selectProductsInBasket,
} from '@ui/products/shopping-cart-back/actions/selector';
import { map } from 'rxjs/operators';
import { PriceConfig } from '@ui/orders/graphql';
import { singleStepPriceCalculator } from '@ui/step-pricing/StepPriceCalculator';
import { StepPricingMode } from '../../../../../../../../photographer-panel/src/graphql';
import { listFilesInProductsInBasket } from '@ui/products/shopping-cart-back/actions/standalone';

@Injectable({
  providedIn: 'root',
})
export class LookBookPriceWithFilesCalculatorService {
  constructor(private store: Store) {}

  calculatePriceForLookBookProduct(
    _productInBasket: {
      price: { min: Array<PriceConfig>; max: Array<PriceConfig> };
      quantity: number;
      photoIdList: string[];
      timeAddedToShoppingCart: number;
    },
    productIndex,
    preCalc = false
  ) {
    const productInBasket = { ..._productInBasket };
    if (
      Array.isArray(productInBasket.photoIdList) &&
      productInBasket.photoIdList.length > 0
    ) {
      productInBasket.photoIdList = [...new Set(productInBasket.photoIdList)];
    }

    return combineLatest([
      this.store.pipe(select(selectLookBookProductsInBasket)),

      this.store.pipe(select(selectPriceToAddToEveryFileInLookBookProduct)),
      this.store.pipe(select(selectPhotosList)),
      this.store.pipe(select(selectPhotoGroups)),
      combineLatest([
        this.store.pipe(select(selectFilesInBasket)),
        this.store.pipe(select(selectFilePrices)),
        this.store.pipe(select(selectPriceToAddToEveryFileInProduct)),
        this.store.pipe(select(selectPriceToAddToFirstPrint)),
        this.store.pipe(select(selectPrintsInBasket)),
        this.store.pipe(select(selectProductsInBasket)),
      ]),
    ]).pipe(
      map(
        ([
          lookBookProductsInBasket,
          priceToAddToEveryFileInLookBookProduct,
          photoGroupList,
          photoGroups,
          [
            filesInBasket,
            filePrices,
            priceToAddToEveryFileInProduct,
            priceToAddToFirstPrint,
            printsInBasket,
            productsInBasket,
          ],
        ]) => {
          const price: Array<PriceConfig> = [];
          productInBasket?.price?.min.forEach((p) => {
            if (price.find((x) => x.code === p.code)) {
              price.find((x) => x.code === p.code).price +=
                p.price * productInBasket.quantity;
            } else {
              price.push({
                code: p.code,
                price: p.price * productInBasket.quantity,
              });
            }
            const filesOutsideBasket = [];
            productInBasket.photoIdList
              .filter((v) => !!v)
              .forEach((photoId) => {
                const config = photoGroups.find(
                  ({ photoGroupId }) =>
                    photoGroupId ===
                    photoGroupList
                      .find(({ photos }) =>
                        photos.find((photo) => photo.photoId === photoId)
                      )
                      .photos.find((photo) => photo.photoId === photoId)
                      .photoGroupId
                )?.config;
                const supplementProduct = config?.individualSellSettings
                  ? config.priceToAddToEveryFileInProduct
                  : priceToAddToEveryFileInProduct;
                if (
                  supplementProduct.addPriceToEveryFileInProduct &&
                  supplementProduct.countWithFilePrices &&
                  ((supplementProduct.skipAddToEveryFileInProductIfFileBought &&
                    !filesInBasket.find(({ photoId: id }) => id === photoId)) ||
                    !supplementProduct.skipAddToEveryFileInProductIfFileBought)
                ) {
                  filesOutsideBasket.push(photoId);
                }
              });
            productInBasket.photoIdList.forEach((photoId) => {
              const config = photoGroups.find(
                ({ photoGroupId }) =>
                  photoGroupId ===
                  photoGroupList
                    .find(({ photos }) =>
                      photos.find((photo) => photo.photoId === photoId)
                    )
                    .photos.find((photo) => photo.photoId === photoId)
                    .photoGroupId
              )?.config;
              if (
                !lookBookProductsInBasket.find(
                  ({ photoIdList: photoList }, i) =>
                    i <
                      (productIndex < 0
                        ? lookBookProductsInBasket.length
                        : productIndex) &&
                    photoList.find((pId) => pId === photoId)
                ) &&
                priceToAddToEveryFileInLookBookProduct.addPriceToEveryFileInProduct &&
                !(
                  (priceToAddToEveryFileInLookBookProduct.skipAddToEveryFileInProductIfFileBought &&
                    filesInBasket.find(
                      (f) =>
                        f.photoId === photoId && !f.togetherWithLookBookProduct
                    )) ||
                  (!priceToAddToEveryFileInLookBookProduct.skipAddToEveryFileInProductIfFileBought &&
                    filesInBasket.find(
                      (f) =>
                        f.photoId === photoId &&
                        !!f.togetherWithPrint &&
                        !!f.togetherWithProduct
                    ))
                )
              ) {
                const fileInProduct = preCalc
                  ? {
                      filePricingId: !!filePrices.length
                        ? filePrices.find(({ isFull }) => !!isFull)
                          ? filePrices.find(({ isFull }) => !!isFull)
                              .filePricingId
                          : filePrices[0].filePricingId
                        : 'togetherWithLookBookProduct',
                    }
                  : filesInBasket.find((f) => f.photoId === photoId);
                const filesPrice =
                  config.individualSellSettings && config.customFilesPricing
                    ? config.files
                    : filePrices;
                let listFilesInProduct: Array<{
                  photoId: string;
                  type: string;
                  timeAddedToShoppingCart: number;
                }>;
                if (
                  priceToAddToEveryFileInLookBookProduct.addPriceToEveryFileInProduct
                ) {
                  listFilesInProduct = listFilesInProductsInBasket(
                    fileInProduct.filePricingId,
                    photoGroups,
                    photoGroupList,
                    priceToAddToEveryFileInProduct,
                    priceToAddToEveryFileInLookBookProduct,
                    priceToAddToFirstPrint,
                    filePrices,
                    printsInBasket,
                    productsInBasket,
                    lookBookProductsInBasket,
                    filesInBasket
                  );
                } else {
                  listFilesInProduct = filesInBasket
                    .filter(
                      (file) =>
                        file.filePricingId === fileInProduct.filePricingId
                    )
                    .map((file) => ({
                      type: 'lookBookProduct',
                      photoId: file.photoId,
                      timeAddedToShoppingCart: file.timeAddedToShoppingCart,
                    }));
                }
                const filePrice = filesPrice.find(
                  ({ filePricingId }) =>
                    filePricingId === fileInProduct?.filePricingId
                );
                const filterListFilesInProduct = listFilesInProduct
                  .filter(
                    ({ photoId: lId, type }) =>
                      (priceToAddToEveryFileInLookBookProduct.skipAddToEveryFileInProductIfFileBought &&
                        (type === 'lookBookProduct' ||
                          (filesInBasket.find((f) => f.photoId === lId) &&
                            !listFilesInProduct.find(
                              (l) =>
                                l.photoId === lId &&
                                l.type === 'lookBookProduct'
                            ) &&
                            type !== 'lookBookProduct'))) ||
                      !priceToAddToEveryFileInLookBookProduct.skipAddToEveryFileInProductIfFileBought
                  )
                  .filter(({ type }) => type === 'lookBookProduct');
                const filterFilesOutsideBasket = [];
                // filesOutsideBasket.filter(
                //   (pId) =>
                //     !listFilesInProduct.find(
                //       ({ photoId: lId, type }) =>
                //         lId === pId && type === 'lookBookProduct'
                //     )
                // );
                if (
                  priceToAddToEveryFileInLookBookProduct.countWithFilePrices &&
                  !!fileInProduct
                ) {
                  price.find(({ code }) => code === p.code).price +=
                    singleStepPriceCalculator(
                      (filterListFilesInProduct.find(
                        (f) =>
                          f.photoId === photoId && f.type === 'lookBookProduct'
                      )
                        ? filterListFilesInProduct.findIndex(
                            (f) =>
                              f.photoId === photoId &&
                              f.type === 'lookBookProduct'
                          )
                        : filterListFilesInProduct.length +
                          filterFilesOutsideBasket.findIndex(
                            (fId) => fId === photoId
                          )) + 1,
                      filterListFilesInProduct.length +
                        filterFilesOutsideBasket.length,
                      !filePrice?.stepPricingEnabled ||
                        filePrice.stepPricing.mode === 'None'
                        ? {
                            ...filePrice?.stepPricing,
                            mode: StepPricingMode.None,
                          }
                        : filePrice?.stepPricing,
                      filePrice.price
                    )?.find(({ code }) => code === p.code)?.price || 0;
                } else {
                  price.find(({ code }) => code === p.code).price +=
                    priceToAddToEveryFileInLookBookProduct.price
                      ? priceToAddToEveryFileInLookBookProduct.price.find(
                          ({ code }) => code === p.code
                        ).price
                      : 0;
                }
              }
            });
          });
          return price;
        }
      )
    );
  }
}
